import React, { useState, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import { db, auth, storage } from '../../firebase/config'; // Import storage
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage'; // Import Firebase Storage functions
import { collection, addDoc, serverTimestamp, doc, getDoc } from 'firebase/firestore';
import toast from 'react-hot-toast';
import {
  PageTitle,
  DropzoneContainer,
  UploadButton,
  Select,
  FilePreviewList,
  FilePreviewItem
} from './DocumentUpload.styles';

const DocumentUpload = () => {
  const [files, setFiles] = useState([]);
  const [category, setCategory] = useState('Sales');
  const [uploading, setUploading] = useState(false);

  const onDrop = useCallback((acceptedFiles) => {
    setFiles(prevFiles => [...prevFiles, ...acceptedFiles]);
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({ 
    onDrop, 
    accept: { 
      'image/*': ['.jpeg', '.png', '.jpg', '.gif'],
      'application/pdf': ['.pdf'],
    },
  });

  const handleUpload = async () => {
    if (files.length === 0) return;
    setUploading(true);
    const loadingToast = toast.loading(`Uploading 1 of ${files.length}...`);

    const user = auth.currentUser;
    const userDocRef = doc(db, "users", user.uid);
    const userDoc = await getDoc(userDocRef);
    const clientName = userDoc.data()?.clientName;

    if (!clientName) {
        toast.error("Client details not found.", { id: loadingToast });
        setUploading(false);
        return;
    }

    let uploadCounter = 0;
    for (const file of files) {
      try {
        // 1. Create the filename and folder path
        const date = new Date().toISOString().split('T')[0].replace(/-/g, '');
        const uniqueId = Math.random().toString(36).substring(2, 9);
        const fileExtension = file.name.split('.').pop();
        const fileName = `${category}_${clientName}_${date}_${uniqueId}.${fileExtension}`;
        const filePath = `users/${user.uid}/${category}/${fileName}`;

        // 2. Create a reference in Firebase Storage
        const storageRef = ref(storage, filePath);

        // 3. Upload the file directly to Firebase Storage
        await uploadBytes(storageRef, file);

        // 4. Get the public download URL
        const downloadURL = await getDownloadURL(storageRef);

        // 5. Create the document record in Firestore
        await addDoc(collection(db, 'documents'), {
            clientName,
            userId: user.uid,
            fileName,
            filePath,       // The path in Firebase Storage for deletion
            downloadURL,    // The public URL for viewing/downloading
            category,
            status: 'Pending',
            uploadedAt: serverTimestamp(),
            tallyNarration: `Ref: ${fileName}`
        });

        uploadCounter++;
        toast.loading(`Uploading ${uploadCounter + 1} of ${files.length}...`, { id: loadingToast });

      } catch (error) {
        toast.error(`Failed to upload ${file.name}.`, { id: loadingToast });
        console.error("Firebase Upload error:", error);
        setUploading(false);
        return;
      }
    }

    toast.success(`Upload complete! ${uploadCounter} file(s) processed.`, { id: loadingToast });
    setFiles([]);
    setUploading(false);
  };

  return (
    <div>
      <PageTitle>Upload Documents</PageTitle>
      <Select value={category} onChange={(e) => setCategory(e.target.value)}>
          <option value="Sales">Sales</option>
          <option value="Purchase">Purchase</option>
          <option value="Bank">Bank</option>
          <option value="Expenses">Expenses</option>
      </Select>
      <DropzoneContainer {...getRootProps()}>
        <input {...getInputProps()} />
        {isDragActive 
          ? <p>Drop files here ...</p> 
          : <p>Drag 'n' drop files here, or click to select files</p>
        }
      </DropzoneContainer>
      {files.length > 0 && (
        <>
          <h4>Ready for Upload:</h4>
          <FilePreviewList>
            {files.map((file, index) => (
              <FilePreviewItem key={`${file.name}-${index}`}>{file.name} - {Math.round(file.size / 1024)} KB</FilePreviewItem>
            ))}
          </FilePreviewList>
        </>
      )}
      <UploadButton onClick={handleUpload} disabled={uploading || files.length === 0}>
        {uploading ? 'Uploading...' : `Upload ${files.length} File(s)`}
      </UploadButton>
    </div>
  );
};

export default DocumentUpload;